import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Search, Trash2, Loader2, X, Clock, CheckCircle, BellOff, Bell, SlidersHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import { ConfirmModal } from '@/components/ConfirmModal';
import { DateInput } from '@/components/ui/DateInput';
import { TableRowSkeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import { extractErrorMessage } from '@/lib/errorMessage';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Floating } from '@/components/ui/Floating';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { formatDateTime } from '@/lib/dateFormat';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { SelectField } from '@/components/ui/SelectField';

type Filter = 'all' | 'today' | 'this-week' | 'overdue';
type ReminderType = 'call' | 'email' | 'follow-up' | 'meeting' | 'other';
type ReminderStatus = 'pending' | 'snoozed' | 'done' | 'dismissed';

interface Reminder {
  id: string; title: string; type: string; notes?: string;
  lead?: { id: string; name: string } | null;
  dueAt: string; status: ReminderStatus; createdAt?: string;
}

interface Lead { id: string; name: string; }

const TYPE_LABELS: Record<string, string> = { call: 'Call', email: 'Email', 'follow-up': 'Follow-up', followup: 'Follow-up', meeting: 'Meeting', other: 'Other' };
const TYPE_COLORS: Record<string, string> = {
  call: 'bg-blue-50 text-blue-700', email: 'bg-purple-50 text-purple-700',
  'follow-up': 'bg-orange-50 text-orange-700', followup: 'bg-orange-50 text-orange-700',
  meeting: 'bg-teal-50 text-teal-700', other: 'bg-gray-100 text-gray-600',
};
const PAGE_SIZES = [10, 25, 50];
const STATUS_COLORS: Record<ReminderStatus, string> = {
  pending: 'bg-yellow-50 text-yellow-700', snoozed: 'bg-blue-50 text-blue-700',
  done: 'bg-emerald-50 text-emerald-700', dismissed: 'bg-gray-100 text-gray-500',
};

// ── Reminder Form Drawer ────────────────────────────────────────────────────────
function ReminderDrawer({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ title: '', type: 'other', notes: '', leadId: '', dueAt: '' });
  const [saving, setSaving] = useState(false);
  const [leadSearch, setLeadSearch] = useState('');
  const [leadResults, setLeadResults] = useState<Lead[]>([]);
  const [showLeadDrop, setShowLeadDrop] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leadDropRef = useRef<HTMLDivElement>(null);

  const searchLeads = (q: string) => {
    setLeadSearch(q);
    if (!q) { setForm(p => ({ ...p, leadId: '' })); setLeadResults([]); return; }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        // POST so the search term (PII) goes in the body, not the URL.
        const { data } = await api.post('/leads/search', { search: q, limit: 10 });
        setLeadResults(Array.isArray(data) ? data : data.leads ?? data.data ?? []);
        setShowLeadDrop(true);
      } catch (err: any) {
        // Search failures are non-blocking — just clear results, no toast on
        // every keystroke. console.warn so it surfaces during dev / Sentry.
        console.warn('Lead search failed:', err?.message);
        setLeadResults([]);
      }
    }, 300);
  };

  const save = async () => {
    if (!form.title.trim() || !form.dueAt) return;
    setSaving(true);
    try {
      await api.post('/reminders', { title: form.title, type: form.type, notes: form.notes || null, leadId: form.leadId || null, dueAt: form.dueAt });
      toast('success', 'Reminder created');
      onSaved();
    } catch (err: any) { toast('error', err?.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent placeholder:text-slate-400';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-[460px] bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-slate-900">New Reminder</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Title <span className="text-red-500">*</span></label>
            <input className={inputCls} placeholder="Reminder title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Type</label>
            <SelectField className={`${inputCls} bg-white`} value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
              {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </SelectField>
          </div>
          <div className="relative" ref={leadDropRef}>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Lead (optional)</label>
            <input className={inputCls} placeholder="Search leads..." value={leadSearch}
              onChange={e => searchLeads(e.target.value)}
              onFocus={() => leadResults.length > 0 && setShowLeadDrop(true)} />
            <Floating open={showLeadDrop && leadResults.length > 0} anchorRef={leadDropRef} onClose={() => setShowLeadDrop(false)}
              className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              <button className="w-full px-3 py-2 text-sm text-left text-slate-500 hover:bg-gray-50"
                onClick={() => { setForm(p => ({ ...p, leadId: '' })); setLeadSearch(''); setShowLeadDrop(false); }}>
                — No Lead
              </button>
              {leadResults.map(l => (
                <button key={l.id} className="w-full px-3 py-2 text-sm text-left hover:bg-indigo-50"
                  onClick={() => { setForm(p => ({ ...p, leadId: l.id })); setLeadSearch(l.name); setShowLeadDrop(false); }}>
                  {l.name}
                </button>
              ))}
            </Floating>
          </div>
          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Due Date & Time <span className="text-red-500">*</span></label>
            <DateTimeInput mode="datetime" value={form.dueAt} onChange={v => setForm(p => ({ ...p, dueAt: v }))} />
          </div>
          <div>
            <label className="block text-xs font-normal text-slate-600 mb-1.5">Notes</label>
            <textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] resize-none placeholder:text-slate-400"
              placeholder="Optional notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 border border-gray-200 hover:border-gray-300">Cancel</button>
          <button onClick={save} disabled={saving || !form.title.trim() || !form.dueAt}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create Reminder
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reminders Page ──────────────────────────────────────────────────────────────
export function RemindersPage() {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Reminder | null>(null);
  // Table filters + paging (client-side; a user's reminders are a short list).
  const [showFilters, setShowFilters] = useState(false);
  const [statusF, setStatusF] = useState('');
  const [typeF, setTypeF] = useState('');
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reminders?filter=${filter}`);
      setReminders(data);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load reminders');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  const action = async (id: string, endpoint: string, label: string) => {
    setActioningId(id);
    try {
      if (endpoint === 'delete') await api.delete(`/reminders/${id}`);
      else await api.patch(`/reminders/${id}/${endpoint}`);
      toast('success', label);
      fetchReminders();
    } catch { toast('error', `Failed: ${label}`); }
    finally { setActioningId(null); }
  };

  const q = search.trim().toLowerCase();
  const filtered = reminders.filter(r => {
    if (q && ![r.title, r.lead?.name, r.notes].some(v => (v ?? '').toLowerCase().includes(q))) return false;
    if (statusF && r.status !== statusF) return false;
    if (typeF && (r.type === 'follow-up' ? 'followup' : r.type) !== typeF) return false;
    if (dueFrom && new Date(r.dueAt) < new Date(`${dueFrom}T00:00:00`)) return false;
    if (dueTo && new Date(r.dueAt) > new Date(`${dueTo}T23:59:59`)) return false;
    return true;
  });
  // No column sort picked → newest entry first (server order).
  const { sorted, sort, onSort } = useSort(filtered);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pageStart = (curPage - 1) * pageSize;
  const pageRows = sorted.slice(pageStart, pageStart + pageSize);
  useEffect(() => { setPage(1); }, [filter, search, statusF, typeF, dueFrom, dueTo, pageSize]);
  const activeFilters = [statusF, typeF, dueFrom, dueTo].filter(Boolean).length;
  const clearFilters = () => { setStatusF(''); setTypeF(''); setDueFrom(''); setDueTo(''); setSearch(''); };
  const fmt = (d: string) => formatDateTime(d);
  const isOverdue = (r: Reminder) => new Date(r.dueAt) < new Date() && r.status === 'pending';

  const FILTERS: { key: Filter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'today', label: 'Today' },
    { key: 'this-week', label: 'This Week' },
    { key: 'overdue', label: 'Overdue' },
  ];

  return (
    <DashboardLayout title="Reminders">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Reminders</h1>
            <p className="text-sm text-slate-500 mt-0.5">Track your follow-ups and scheduled actions</p>
          </div>
          <button onClick={() => setShowDrawer(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold transition-colors">
            <Plus className="h-4 w-4" /> Add Reminder
          </button>
        </div>

        {/* Filter tabs + search */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filter === f.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, lead or notes..."
              className="w-full h-10 pl-9 pr-4 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-[#6366F1]" />
          </div>
          <button onClick={() => setShowFilters(v => !v)}
            className={`inline-flex items-center gap-2 h-10 px-3 rounded-lg border text-sm transition-colors ${showFilters || activeFilters ? 'border-[#6366F1] text-[#6366F1] bg-indigo-50/50' : 'border-gray-200 text-slate-600 hover:bg-gray-50'}`}>
            <SlidersHorizontal className="h-4 w-4" /> Filters
            {activeFilters > 0 && <span className="rounded-full bg-[#6366F1] px-1.5 text-[10px] font-semibold text-white">{activeFilters}</span>}
          </button>
          {(activeFilters > 0 || search) && (
            <button onClick={clearFilters} className="inline-flex items-center gap-1 h-10 px-2 text-sm text-slate-500 hover:text-slate-700">
              <X className="h-4 w-4" /> Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs text-slate-500">Status</label>
              <SelectField value={statusF} onChange={e => setStatusF(e.target.value)} className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm">
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="snoozed">Snoozed</option>
                <option value="done">Done</option>
                <option value="dismissed">Dismissed</option>
              </SelectField>
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Type</label>
              <SelectField value={typeF} onChange={e => setTypeF(e.target.value)} className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm">
                <option value="">All types</option>
                <option value="followup">Follow-up</option>
                <option value="call">Call</option>
                <option value="email">Email</option>
                <option value="meeting">Meeting</option>
                <option value="other">Other</option>
              </SelectField>
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Due from</label>
              <DateInput value={dueFrom} onChange={setDueFrom} max={dueTo || undefined} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Due to</label>
              <DateInput value={dueTo} onChange={setDueTo} min={dueFrom || undefined} />
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-50">
            <span className="text-xs text-slate-400">{loading ? 'Loading…' : `${filtered.length} reminder${filtered.length !== 1 ? 's' : ''}${filtered.length !== reminders.length ? ` of ${reminders.length}` : ''}`}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <TableHeadRow>
                  <SortTh label="S.No" sortable={false} className="w-14" />
                  <SortTh label="Title" sortKey="title" sort={sort} onSort={onSort} />
                  <SortTh label="Type" sortKey="type" sort={sort} onSort={onSort} />
                  <SortTh label="Lead" sortKey="lead.name" sort={sort} onSort={onSort} />
                  <SortTh label="Due Date" sortKey="dueAt" sort={sort} onSort={onSort} />
                  <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                  <SortTh label="Actions" sortable={false} />
                </TableHeadRow>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => <TableRowSkeleton key={i} columns={7} />)
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4">
                      <EmptyState
                        icon={Bell}
                        title={reminders.length === 0 ? 'No reminders' : 'No reminders match'}
                        description={reminders.length === 0 ? 'Reminders attached to leads or tasks will surface here.' : 'Try clearing the filters or search.'}
                      />
                    </td>
                  </tr>
                ) : pageRows.map((r, idx) => {
                  const overdue = isOverdue(r);
                  const busy = actioningId === r.id;
                  return (
                    <tr key={r.id} className={`hover:bg-gray-50/80 transition-colors ${overdue ? 'bg-red-50/30' : ''}`}>
                      <td className="px-4 py-3 text-xs text-slate-400">{pageStart + idx + 1}</td>
                      <td className="px-4 py-3">
                        <p className={`font-medium ${overdue ? 'text-red-600' : 'text-slate-800'}`}>{r.title}</p>
                        {r.notes && <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[200px]">{r.notes}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[r.type] ?? TYPE_COLORS.other}`}>
                          {TYPE_LABELS[r.type] ?? r.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.lead?.name ?? <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={overdue ? 'text-red-600 font-medium' : 'text-slate-600'}>{fmt(r.dueAt)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[r.status]}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {busy ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
                            <>
                              {r.status !== 'done' && (
                                <button title="Mark Done" onClick={() => action(r.id, 'done', 'Marked done')}
                                  className="p-1.5 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600">
                                  <CheckCircle className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {r.status === 'pending' && (
                                <button title="Snooze 15min" onClick={() => action(r.id, 'snooze', 'Snoozed 15 min')}
                                  className="p-1.5 rounded hover:bg-blue-50 text-slate-400 hover:text-blue-600">
                                  <Clock className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {r.status !== 'dismissed' && r.status !== 'done' && (
                                <button title="Dismiss" onClick={() => action(r.id, 'dismiss', 'Dismissed')}
                                  className="p-1.5 rounded hover:bg-gray-100 text-slate-400 hover:text-slate-600">
                                  <BellOff className="h-3.5 w-3.5" />
                                </button>
                              )}
                              <button title="Delete" onClick={() => setConfirmDelete(r)}
                                className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paging */}
          {!loading && sorted.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-3 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <span>Rows per page</span>
                <SelectField value={String(pageSize)} onChange={e => setPageSize(Number(e.target.value))} className="h-8 w-20 rounded-md border border-gray-200 px-2 text-sm">
                  {PAGE_SIZES.map(n => <option key={n} value={String(n)}>{n}</option>)}
                </SelectField>
                <span className="ml-2">Showing {pageStart + 1}–{Math.min(pageStart + pageSize, sorted.length)} of {sorted.length}</span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(1)} disabled={curPage === 1} className="h-8 rounded-md border border-gray-200 px-2.5 text-xs disabled:opacity-40">First</button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={curPage === 1} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 disabled:opacity-40" aria-label="Previous page"><ChevronLeft className="h-4 w-4" /></button>
                <span className="px-2">Page {curPage} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={curPage === totalPages} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 disabled:opacity-40" aria-label="Next page"><ChevronRight className="h-4 w-4" /></button>
                <button onClick={() => setPage(totalPages)} disabled={curPage === totalPages} className="h-8 rounded-md border border-gray-200 px-2.5 text-xs disabled:opacity-40">Last</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Delete reminder?"
        message={`Delete "${confirmDelete?.title ?? ''}"? This cannot be undone.`}
        onConfirm={() => { const r = confirmDelete; setConfirmDelete(null); if (r) action(r.id, 'delete', 'Deleted'); }}
        onCancel={() => setConfirmDelete(null)}
      />

      {showDrawer && <ReminderDrawer onClose={() => setShowDrawer(false)} onSaved={() => { setShowDrawer(false); fetchReminders(); }} />}
    </DashboardLayout>
  );
}
